// Single source of truth for preset-menu product lines, grouped by Format.
// The create-preset-menu modal renders these as grouped, searchable sections;
// the list table renders a single label via PRODUCT_LINE_LABELS.
//
// GRAIN-2473: the box formats are folded UNDER Mini Buffet — their product
// lines are added to the Mini Buffet group so picking Mini Buffet surfaces
// them (mirrors Product::FORM_PRODUCT_LINES[MINI_BUFFET]). The standalone
// Gathering Box / Meal Box / Treat Box groups are ALSO kept: preset menus
// still support all formats, and productLinesForForm('meal_box') must return
// the meal-box lines (mirrors Product::ProductLines.for_form). Only the
// *event* format dropdown is limited to EVENT_FORM_VALUES; preset-menu
// create/edit keep every format.

export interface ProductLineOption {
  /** Backend key — persisted and sent on the wire. */
  value: string;
  label: string;
}

export interface ProductLineGroup {
  /** Human-readable group name shown as the tab label and section heading. */
  service: string;
  /** Product::Forms key — matches the `form` field value on the wire. */
  form: string;
  options: ProductLineOption[];
}

export const PRODUCT_LINE_GROUPS: ProductLineGroup[] = [
  {
    service: 'Buffet',
    form: 'buffet',
    options: [
      { value: 'breakfast_buffet', label: 'Breakfast Buffet' },
      { value: 'lunch_buffet', label: 'Lunch Buffet' },
      { value: 'dinner_buffet', label: 'Dinner Buffet' },
      { value: 'high_tea_buffet', label: 'High Tea Buffet' },
      { value: 'half_day_seminar_buffet', label: 'Half-day Seminar Buffet' },
      { value: 'full_day_seminar_buffet', label: 'Full-day Seminar Buffet' },
      { value: 'canapes', label: 'Canapés' },
      { value: 'live_station', label: 'Live Station' },
    ],
  },
  {
    // GRAIN-2473: Meal Box / Treat Box are hidden from the format dropdown and
    // folded under Mini Buffet, so their product lines live in this group.
    // Gathering Box is NOT folded here — it is now its own event format (it has
    // its own standalone group below), so offering it under Mini Buffet too would
    // duplicate the line. The backend FORM_PRODUCT_LINES[MINI_BUFFET] still keeps
    // gathering_box for backward-compat with legacy mini_buffet events.
    service: 'Mini Buffet',
    form: 'mini_buffet',
    options: [
      { value: 'mini_breakfast_buffet', label: 'Mini Breakfast Buffet' },
      { value: 'mini_high_tea_buffet', label: 'Mini High Tea Buffet' },
      { value: 'lunch_mini_buffet', label: 'Lunch Mini Buffet' },
      { value: 'dinner_mini_buffet', label: 'Dinner Mini Buffet' },
      { value: 'half_day_seminar_meal_box', label: 'Half-day Seminar Meal Box' },
      { value: 'full_day_seminar_meal_box', label: 'Full-day Seminar Meal Box' },
      { value: 'regular', label: 'Regular Meal Box' },
      { value: 'side_box', label: 'Side Box' },
      { value: 'treat_box', label: 'Treat Box' },
    ],
  },
  // Standalone box groups — hidden from new creation but retained so existing
  // box-format preset menus keep their product-line options on the edit form.
  {
    service: 'Gathering Box',
    form: 'gathering_box',
    options: [{ value: 'gathering_box', label: 'Gathering Box' }],
  },
  {
    service: 'Meal Box',
    form: 'meal_box',
    options: [
      { value: 'half_day_seminar_meal_box', label: 'Half-day Seminar Meal Box' },
      { value: 'full_day_seminar_meal_box', label: 'Full-day Seminar Meal Box' },
      { value: 'regular', label: 'Regular Meal Box' },
      { value: 'side_box', label: 'Side Box' },
    ],
  },
  {
    service: 'Treat Box',
    form: 'treat_box',
    options: [{ value: 'treat_box', label: 'Treat Box' }],
  },
];

// Flat key -> label map for the list table cell and the picker trigger.
export const PRODUCT_LINE_LABELS: Record<string, string> = Object.fromEntries(
  PRODUCT_LINE_GROUPS.flatMap((group) => group.options.map((o) => [o.value, o.label])),
);

/**
 * Returns all product-line values valid for a given form key.
 * Mirrors Product::ProductLines.for_form(form) on the backend — the single
 * source for both the picker's form filter and the form-change clear logic.
 * @param form - Product::Forms key (e.g. "buffet", "mini_buffet")
 * @returns the form's product-line keys, or [] for an unknown/blank form
 */
export function productLinesForForm(form: string): string[] {
  const group = PRODUCT_LINE_GROUPS.find((g) => g.form === form);
  return group ? group.options.map((o) => o.value) : [];
}
